// MeetingDebriefingRoom.js

import React, { Fragment, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon as XIcon,
  CalendarIcon,
  ClockIcon,
  PhoneIcon,
  UserGroupIcon,
  CheckIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useTheme } from "../../../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useDemo, clearDemoStorage, setCompletedDemo } from "../../../contexts/DemoContext";
import logger from '../../../utils/logger';
import Modal from "../../../components/shared/Modal";
import styled from "styled-components";

/**
 * Quadrant & InfoItem components (copied from MeetingBriefingRoom.js)
 * so that each "box" shares the same blurred‐glass styling.
 */
const Quadrant = ({ title, icon: Icon, children, className = "" }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`${
      isDarkMode 
        ? 'bg-surface-default dark:bg-surface-default/10' 
        : 'bg-white'
    } backdrop-blur-sm rounded-lg p-2 shadow-sm dark:shadow-none ${className}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} />
        <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
      </div>
      <div className="overflow-y-auto">{children}</div>
    </div>
  );
};

const InfoItem = ({ icon: Icon, label, value, onClick, className = "" }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-700'} mb-1 text-xs ${className}`}>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-500'}>{label}:</span>
      {onClick ? (
        <button
          onClick={onClick}
          className={`font-medium ${
            isDarkMode 
              ? 'hover:text-blue-300' 
              : 'hover:text-blue-500'
          } transition-colors`}
        >
          {value}
        </button>
      ) : (
        <span className="font-medium">{value}</span>
      )}
    </div>
  );
};

// TaskCard with green/gray circle + check, matching DiscussionPoint style
const TaskCard = ({ text, done, onToggle }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`${
      isDarkMode 
        ? 'bg-surface-default/10 dark:bg-surface-default/10' 
        : 'bg-white'
    } backdrop-blur-sm rounded-lg p-1.5 flex items-center justify-between shadow-sm dark:shadow-none`}>
      {/* Left side: green/gray circle + task text */}
      <div className="flex items-center gap-1">
        <div 
          onClick={onToggle}
          className={`
            w-3.5 h-3.5 aspect-square rounded-full border-2
            flex-shrink-0 flex-none
            transition-all duration-200 flex items-center justify-center
            ${done
              ? "bg-emerald-500 border-emerald-500 ring-2 ring-emerald-500/20"
              : `${isDarkMode ? 'bg-transparent' : 'bg-white'} ${
                  isDarkMode 
                    ? 'border-gray-500 hover:border-emerald-400' 
                    : 'border-gray-400 hover:border-emerald-400'
                }`
            } hover:ring-2 hover:ring-emerald-300/30
          `}
        >
          {done && <CheckIcon className="w-2.5 h-2.5 text-white" />}
        </div>

        {/* Task text: line-through if done */}
        <span 
          onClick={onToggle}
          className={`${isDarkMode ? 'text-white' : 'text-gray-900'} ${done ? "line-through opacity-70" : ""} cursor-pointer text-xs`}
        >
          {text}
        </span>
      </div>

      {/* Right side: arrow chevron */}
      <button className={`${
        isDarkMode 
          ? 'text-blue-300 hover:text-blue-200' 
          : 'text-blue-500 hover:text-blue-600'
      }`}>
        <ChevronRightIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// Updated task arrays
const advisorTasks = [
  { id: 1, text: "Send risk assessment questionnaire link", done: true },
  { id: 2, text: "Review and Prepare PE fund analysis report", done: false },
  {
    id: 3,
    text: "Update retirement projections with new RSU data",
    done: false,
  },
  { id: 4, text: "Reassess early retirement options", done: false },
  { id: 5, text: "Send Tax Analysis & Meeting Recap", done: false },
];

const clientTasks = [
  {
    id: 6,
    text: "Complete household risk assessment questionnaire",
    done: false,
  },
  {
    id: 7,
    text: "Schedule CPA consultation for Roth conversion strategy",
    done: false,
  },
  { id: 8, text: "Future revisit of estate planning", done: false },
];

/**
 * Structured "Call Summary" data.
 * Each section has an emoji, a title, and a non‐empty array of items.
 * Any section with an empty items[] is automatically omitted.
 */
const SUMMARY_SECTIONS = [
  {
    emoji: "👤",
    title: "Personal & Life Updates",
    items: [
      "Thomas promoted to Senior Director of Engineering",
      "Significant RSU grant received",
      "Considering early retirement at 64",
      "Recent travel to Switzerland",
      "Both in good health",
    ],
  },
  {
    emoji: "💰",
    title: "Income & Cash Flow",
    items: [
      "2025 Income: $141,787.52",
      "Monthly Net Gain: +$2,300",
      "Strong cash reserves maintained",
    ],
  },
  {
    emoji: "📈",
    title: "Balance Sheet",
    items: [
      "Mortgage remains only liability",
      "Strong asset growth trend",
      "2,500 RSUs (2026–2030 vesting)",
    ],
  },
  {
    emoji: "🛡️",
    title: "Risk Assessment",
    items: [
      // Empty → will be filtered out automatically
    ],
  },
  {
    emoji: "🏖️",
    title: "Retirement Planning",
    items: [
      "Early retirement (64) feasible",
      "95% confidence in sustainability",
      "Score: 91/94 on projections",
    ],
  },
  {
    emoji: "📊",
    title: "Investment Strategy",
    items: ["PE funds proposed", "Pending risk assessment results"],
  },
  {
    emoji: "🧾",
    title: "Tax Planning",
    items: [
      "$40k Roth conversion proposed",
      "10% buffer included",
      "CPA confirmation needed",
    ],
  },
  {
    emoji: "🏠",
    title: "Estate Planning",
    items: [
      "Review pending retirement decision",
      "Streamlined service available",
    ],
  },
];

const DebriefingRoomContent = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { stopDemo, selectedJourney } = useDemo();
  const completeButtonRef = useRef(null);

  console.log('[DebriefingRoomContent] Rendered');

  // Update spotlight effect setup
  useEffect(() => {
    if (!completeButtonRef.current) return;

    const timer = setTimeout(() => {
      const event = new CustomEvent('spotlight', {
        detail: {
          target: completeButtonRef.current,
          label: "Click 'Complete Meeting' to finish the demo and return to the dashboard",
          showCloseButton: false,
          disableAutoAdvance: true,
          modalType: "MEETING_DEBRIEF",  // Add modalType to spotlight event
          allowedElements: ["#modal-complete-btn-meeting-debrief"]  // Explicitly allow the button
        }
      });
      window.dispatchEvent(event);
    }, 500);

    return () => {
      clearTimeout(timer);
      // Clean up spotlight when component unmounts
      window.dispatchEvent(new CustomEvent('spotlight-cleanup', {
        detail: { modalType: "MEETING_DEBRIEF" }
      }));
    };
  }, [completeButtonRef.current]);

  // Add state for managing task completion - initialize with all tasks
  const [advisorTaskStates, setAdvisorTaskStates] = useState(advisorTasks);
  const [clientTaskStates, setClientTaskStates] = useState(clientTasks);

  // Handler for toggling advisor tasks
  const toggleAdvisorTask = (taskId) => {
    setAdvisorTaskStates(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId ? { ...task, done: !task.done } : task
      )
    );
  };

  // Handler for toggling client tasks
  const toggleClientTask = (taskId) => {
    setClientTaskStates(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId ? { ...task, done: !task.done } : task
      )
    );
  };

  const handleConfirmDebrief = () => {
  // Log completion
    logger.info("Meeting completed, ending demo.");
  try { localStorage.setItem(`demo:schedule:completed:${selectedJourney?.id || "annual-review"}`, "1"); } catch(e) {}
    
    // 1) Mark journey as completed if exists
    if (selectedJourney) {
      setCompletedDemo(selectedJourney);
    }
    
    // 2) Close modal
    onClose();
    window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));
    
    // 3) Stop demo first, then navigate home
    stopDemo();
    clearDemoStorage();
    setTimeout(() => {
      navigate('/home-dashboard', { replace: true });
    }, 200);
  };

  // 1) Compute "today + 21 days"
  const raw = new Date();
  raw.setDate(raw.getDate() + 21);
  const nextMeetingDate = raw.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // 2) Filter out any section whose items[] is empty:
  const filteredSections = SUMMARY_SECTIONS.filter(
    (sec) => sec.items.length > 0,
  );

  // 3) Split filteredSections into three columns based on specific groupings
  const column1Sections = filteredSections.filter(sec => 
    ["Personal & Life Updates", "Retirement Planning"].includes(sec.title)
  );
  
  const column2Sections = filteredSections.filter(sec => 
    ["Income & Cash Flow", "Balance Sheet", "Investment Strategy"].includes(sec.title)
  );
  
  const column3Sections = filteredSections.filter(sec => 
    ["Tax Planning", "Estate Planning"].includes(sec.title)
  );

  // Placeholder values (replace with real data/context as needed)
  const householdName = "Borer Household";
  const meetingTime = "8:00 AM – 9:30 AM (1 hr 30 min)";
  const meetingMedium = "Phone Call";
  const bestContact = "(979) 931-6078";

  const handleReschedule = () => {
    // TODO: open scheduling modal
    logger.info("Reschedule clicked.");
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-1.5 max-h-[calc(100vh-12rem)] overflow-y-auto">
        {/* Meeting Overview */}
        <Quadrant title="Meeting Overview" icon={CalendarIcon}>
          <div className="space-y-2">
            <InfoItem
              icon={CalendarIcon}
              label="Title"
              value={`Annual Review Meeting – ${householdName}`}
              className="text-xs"
            />
            <InfoItem
              icon={CalendarIcon}
              label="Date"
              value={nextMeetingDate}
              className="text-xs"
            />
            <InfoItem 
              icon={ClockIcon} 
              label="Time" 
              value={meetingTime}
              className="text-xs"
            />
            <InfoItem
              icon={PhoneIcon}
              label="Medium"
              value={
                <div className="flex items-center gap-1">
                  <span>{meetingMedium}</span>
                  <button
                      onClick={() => {
                        try {
                          const id = selectedJourney?.id || "annual-review";
                          localStorage.setItem(`demo:schedule:completed:${id}`, "1");
                        } catch (e) {}
                        /* TODO: toggle video/phone */
                      }}
                  >
                    Switch to Video
                  </button>
                </div>
              }
              className="text-xs"
            />
            <InfoItem
              icon={PhoneIcon}
              label="Best Contact"
              value={bestContact}
              className="text-xs"
            />
            <div className="mt-2">
              <h4 className="text-xs text-gray-600 dark:text-gray-300 mb-1">Attendees:</h4>
              <div className="flex items-center gap-1 text-xs text-gray-900 dark:text-white">
                <UserGroupIcon className="w-3.5 h-3.5" />
                <span>Thomas &amp; Mary Borer, Advisor (You)</span>
              </div>
            </div>
            <div className="mt-2 flex gap-1">
              <button
                onClick={handleConfirmDebrief}
                className="flex-1 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
              >
                Confirm Meeting
              </button>
              <button
                onClick={handleReschedule}
                className="flex-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Reschedule
              </button>
            </div>
          </div>
        </Quadrant>

        {/* Advisor: Follow Up Tasks */}
        <Quadrant title="Advisor: Follow Up Tasks" icon={CheckIcon}>
          <div className="space-y-1">
            {advisorTaskStates.map((task) => (
              <TaskCard
                key={task.id}
                text={task.text}
                done={task.done}
                onToggle={() => toggleAdvisorTask(task.id)}
              />
            ))}
          </div>
        </Quadrant>

        {/* Client: Follow Up Tasks */}
        <Quadrant title="Client: Follow Up Tasks" icon={CheckIcon}>
          <div className="space-y-1">
            {clientTaskStates.map((task) => (
              <TaskCard
                key={task.id}
                text={task.text}
                done={task.done}
                onToggle={() => toggleClientTask(task.id)}
              />
            ))}
          </div>
        </Quadrant>

        {/* Call Summary */}
        <div className={`col-span-3 ${
          isDarkMode 
            ? 'bg-surface-default/10' 
            : 'bg-white'
        } backdrop-blur-sm rounded-lg p-2 overflow-hidden shadow-sm dark:shadow-none`}>
          <div className="flex items-center gap-1.5 mb-2">
            <UserGroupIcon className={`w-3.5 h-3.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} />
            <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Call Summary</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {/* Column 1 */}
            <div className="space-y-2">
              {column1Sections.map((sec, idx) => (
                <div key={idx}>
                  <h4 className={`flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-gray-900'} text-xs font-semibold`}>
                    <span className="text-sm">{sec.emoji}</span>
                    {sec.title}
                  </h4>
                  <ul className={`list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {sec.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Column 2 */}
            <div className="space-y-2">
              {column2Sections.map((sec, idx) => (
                <div key={idx}>
                  <h4 className={`flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-gray-900'} text-xs font-semibold`}>
                    <span className="text-sm">{sec.emoji}</span>
                    {sec.title}
                  </h4>
                  <ul className={`list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {sec.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Column 3 */}
            <div className="space-y-2">
              {column3Sections.map((sec, idx) => (
                <div key={idx}>
                  <h4 className={`flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-gray-900'} text-xs font-semibold`}>
                    <span className="text-sm">{sec.emoji}</span>
                    {sec.title}
                  </h4>
                  <ul className={`list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {sec.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Complete Meeting Button */}
      <div className="mt-4 px-4">
        <button
          ref={completeButtonRef}
          onClick={handleConfirmDebrief}
          className="w-full px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors flex items-center justify-center gap-1 font-medium"
          data-spotlight-target="true"
          data-modal-type="MEETING_DEBRIEF"
          id="modal-complete-btn-meeting-debrief"
        >
          <CheckIcon className="w-3.5 h-3.5" />
          Complete Meeting
        </button>
      </div>
    </>
  );
};

DebriefingRoomContent.propTypes = {
  onClose: PropTypes.func.isRequired,
};

const MeetingDebriefingRoom = ({ isOpen, onClose }) => {
  const { isDarkMode } = useTheme();

  console.log('[MeetingDebriefingRoom] Rendered with props:', { isOpen, onClose });

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      width="xl"
      modalType="MEETING_DEBRIEF"
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Meeting Debriefing Room
        </h3>
        <button
          onClick={onClose}
          className={`rounded-full p-1 ${
            isDarkMode 
              ? 'hover:bg-gray-700 bg-gray-800/30' 
              : 'hover:bg-gray-100'
          } transition-colors`}
        >
          <XIcon className={`w-3.5 h-3.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />
        </button>
      </div>
      <DebriefingRoomContent onClose={onClose} />
    </Modal>
  );
};

MeetingDebriefingRoom.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MeetingDebriefingRoom;
